import BoardCanvas from './BoardCanvas';
import { Box } from '@mui/material';
import BoardCanvasSelectionOverlay from 'boardCanvas/BoardCanvasSelectionOverlay';
import { MutableRefObject, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
  BoardType,
  Material,
  Renderable,
  RenderableMetadata,
  SneezeGuardState,
  Units,
} from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import { settingsAtom } from 'state/recoilState';
import { degToRad, scaleDimensions } from 'utils/utils';

function BoardCanvasWithOverlays<T extends BoardType>(props: {
  boardInstance: BoardBase<T>;
  canvasRef?: MutableRefObject<HTMLCanvasElement | null>;
  onDrag: (snState: SneezeGuardState<T>) => void;
  onDragEnd: () => void;
  onDragStart: () => void;
  onSelectionChange: (selection: null | RenderableMetadata<T>) => void;
  onSelectedFragmentMaterialChange: (
    material: Material,
    renderable: Renderable<T>
  ) => void;
  onSelectedFragmentSizeChange: (
    size: number,
    dimension: 'height' | 'width',
    renderable: Renderable<T>
  ) => void;
  rotation?: number;
  selection: null | RenderableMetadata<T>;
}) {
  const {
    boardInstance,
    canvasRef,
    onDrag,
    onDragEnd,
    onDragStart,
    onSelectionChange,
    onSelectedFragmentMaterialChange,
    onSelectedFragmentSizeChange,
    rotation = 0,
    selection,
  } = props;

  const [transitionCount, setTransitionCount] = useState(0);

  const settings = useRecoilValue(settingsAtom);

  const dimensions = boardInstance.getDimensions();
  const renderables = boardInstance.getRenderables();

  const pxPerInch =
    settings.units === Units.IN
      ? settings.pxPerInch
      : settings.pxPerInch / 25.4;
  const scaleFactor =
    boardInstance.units === Units.IN ? pxPerInch : pxPerInch / 25.4;
  const scaledDimensions = scaleDimensions(dimensions, scaleFactor);

  const rotationInRad = degToRad(rotation);
  const rotatedHeight =
    Math.abs(Math.cos(rotationInRad)) * scaledDimensions.height +
    Math.abs(Math.sin(rotationInRad)) * scaledDimensions.width;
  const rotatedWidth =
    Math.abs(Math.cos(rotationInRad)) * scaledDimensions.width +
    Math.abs(Math.sin(rotationInRad)) * scaledDimensions.height;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: rotatedHeight,
        width: rotatedWidth,
        position: 'relative',
        transform: `rotate(${rotation}deg)`,
        transition: 'all 300ms ease',
      }}
      onTransitionEnd={() => setTransitionCount((count) => count + 1)}
    >
      <BoardCanvas
        canvasRef={canvasRef}
        dimensions={dimensions}
        renderables={renderables}
        scaleFactor={
          boardInstance.units === Units.IN ? pxPerInch : pxPerInch / 25.4
        }
      />
      <Box sx={{ display: 'flex', position: 'absolute' }}>
        <BoardCanvasSelectionOverlay
          height={scaledDimensions.height}
          width={scaledDimensions.width}
          board={boardInstance}
          rotation={rotation}
          onDrag={onDrag}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          onSelectionChange={onSelectionChange}
          onSelectedFragmentMaterialChange={onSelectedFragmentMaterialChange}
          onSelectedFragmentSizeChange={onSelectedFragmentSizeChange}
          selection={selection}
          transitionCount={transitionCount}
        ></BoardCanvasSelectionOverlay>
      </Box>
    </Box>
  );
}

export default BoardCanvasWithOverlays;
